/**
 * ArticlePdfView — PDF-enriched article content for the "Deep Dive" tab.
 *
 * Layout (avoids duplicating the Summary/abstract tab):
 *   1. Enhanced Summary (plain_summary narrative + key_findings bullets)
 *   2. Participants | Dosing  (2-col grid on desktop, stacked on mobile)
 *   3. Primary Outcome / Biomarker (how the study measured its endpoint + CI)
 *   4. Safety (conclusion or AE table, contraindications, drug interactions)
 *   5. Clinical Takeaway (effect size badge + clinical insights)
 *   6. Funding & Conflicts (conditional — only if worth disclosing)
 *   7. Study Methodology (collapsible: labeled design rows, stats, subgroups, limitations)
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Collapse,
} from '@mui/material';
import GroupOutlined from '@mui/icons-material/GroupOutlined';
import MedicationOutlined from '@mui/icons-material/MedicationOutlined';
import AssessmentOutlined from '@mui/icons-material/AssessmentOutlined';
import ExpandMore from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LightbulbOutlined from '@mui/icons-material/LightbulbOutlined';
import { alpha } from '@mui/material/styles';
import { ShieldOutlined, CheckCircleOutline } from '../../icons';
import { color, tabularNums } from '../../theme/tokens';

// ── Shared styles ────────────────────────────────────────────────

const sectionLabel = {
  fontSize: '0.7rem',
  fontWeight: 600,
  color: 'text.secondary',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  mb: 0.5,
};

const bodyText = {
  fontSize: '0.9rem',
  color: 'text.primary',
  lineHeight: 1.7,
};

const secondaryText = {
  fontSize: '0.85rem',
  color: 'text.secondary',
  lineHeight: 1.6,
};

const cardSx = {
  py: 1.5,
  px: 2,
  backgroundColor: 'grey.50',
  borderRadius: '0.5rem',
  border: '0.0625rem solid',
  borderColor: 'divider',
};

const iconSx = { fontSize: '1rem', color: 'text.secondary', flexShrink: 0 };

// ── Pure helpers ─────────────────────────────────────────────────

function parseJson(val) {
  if (!val) return null;
  if (typeof val === 'object') return val;
  try { return JSON.parse(val); } catch { return null; }
}

// Convert duration_weeks: 0.14 → "1 day", 1 → "1 week", 8 → "8 weeks"
function fmtDuration(w) {
  if (w == null) return null;
  const weeks = parseFloat(w);
  if (isNaN(weeks)) return null;
  if (weeks < 1) {
    const days = Math.round(weeks * 7);
    return days <= 1 ? '1 day' : `${days} days`;
  }
  return `${weeks} week${weeks !== 1 ? 's' : ''}`;
}

// Convert attrition decimal fraction to percent string: 0.15 → "15%"
function fmtRate(r) {
  if (r == null) return null;
  const n = parseFloat(r);
  if (isNaN(n)) return null;
  return n < 1 ? `${Math.round(n * 100)}%` : `${n}%`;
}

// Convert boolean (or string "true"/"false") to "Yes" / "No"
function boolToYesNo(v) {
  if (v === true || v === 'true') return 'Yes';
  if (v === false || v === 'false') return 'No';
  return null;
}

// Return false for values the pipeline emits as missing (null, "null", "N/A", etc.)
function isValidValue(v) {
  if (v == null) return false;
  const s = String(v).trim().toLowerCase();
  return s !== '' && s !== 'null' && s !== 'n/a' && s !== 'not reported' && s !== 'not stated';
}

// Convert ci_95 array to a plain-language reliability statement.
// Only the zero-crossing check is reliable without knowing the outcome scale.
function ciToPlainLanguage(ci) {
  if (!Array.isArray(ci) || ci.length !== 2 || ci[0] == null || ci[1] == null) return null;
  const lower = parseFloat(ci[0]);
  const upper = parseFloat(ci[1]);
  if (isNaN(lower) || isNaN(upper)) return null;
  if (lower < 0 && upper > 0) return 'Result is uncertain — effect may not be real';
  return 'Result is consistent with an effect';
}

// Attrition rate → plain-language dropout context.
function attritionLabel(rate) {
  if (rate == null) return null;
  const n = parseFloat(rate);
  if (isNaN(n)) return null;
  const pct = n < 1 ? n * 100 : n;
  if (pct <= 10) return 'Very low dropout — results are reliable';
  if (pct <= 20) return 'Moderate dropout — results are largely reliable';
  return 'High dropout — results should be interpreted with caution';
}

// Parse clinical_significance: "large (Cohen's d = 1.02)" → { magnitude, detail }
function parseClinicalSignificance(cs) {
  if (!cs) return null;
  const lower = cs.toLowerCase();
  let magnitude;
  if (lower.includes('not significant')) magnitude = 'not significant';
  else if (lower.startsWith('large')) magnitude = 'large';
  else if (lower.startsWith('moderate')) magnitude = 'moderate';
  else if (lower.startsWith('small')) magnitude = 'small';
  else magnitude = null;

  const parenStart = cs.indexOf('(');
  const parenEnd = cs.lastIndexOf(')');
  const detail = parenStart !== -1 && parenEnd > parenStart
    ? cs.slice(parenStart + 1, parenEnd)
    : null;
  return { magnitude, detail };
}

// Known clinical scale acronyms and proper nouns that should never be labelled "Lab-verified".
// Intentionally excludes generic words like "test", "score", "scale", "index" — those appear
// inside legitimate lab test names (e.g. "immunoturbidimetric test", "Tyndall index") and
// would cause false reclassifications.  Only add terms that are unambiguously assessment tools.
const CLINICAL_SCALE_ACRONYMS = [
  // Neurology / stroke
  'nihss', 'glasgow coma', 'rankin', 'barthel', 'mmse', 'moca', 'mini-mental',
  // Pain / function
  'oswestry', ' odi ', 'womac', 'koos', 'karnofsky', 'lansky',
  // Mental health
  'phq-', 'gad-', 'ham-a', 'ham-d', 'hdrs', 'beck depression', 'beck anxiety',
  'columbia suicide', 'columbia-suicide', 'panss', 'bprs', 'madrs',
  // Quality of life / general
  'sf-36', 'sf-12', 'eq-5d', 'whoqol', 'promis',
  // Generic scale descriptors that are unambiguous in context
  'questionnaire', 'disability questionnaire', 'rating scale', 'assessment scale',
];

// Parse measurement method prefix → { type, label, text }
// Handles both structured pipeline prefixes and common free-text variants the pipeline emits.
// Includes a post-parse correction: if the pipeline labels something "Laboratory-verified"
// but the text contains clinical scale keywords, reclassify as "Validated scale".
function parseMeasurementMethod(method) {
  if (!method) return null;

  const prefixMap = [
    // Structured prefixes (canonical pipeline output)
    { prefix: 'Laboratory-verified:', type: 'lab',      label: 'Lab-verified'       },
    { prefix: 'Clinician-assessed:', type: 'clinician', label: 'Clinician-assessed' },
    { prefix: 'Validated scale:',    type: 'scale',     label: 'Validated scale'    },
    { prefix: 'Self-reported:',      type: 'self',      label: 'Self-reported'      },
    // Free-text variants the pipeline currently emits
    { prefix: 'Clinical assessment:', type: 'clinician', label: 'Clinician-assessed' },
    { prefix: 'Clinician assessment:', type: 'clinician', label: 'Clinician-assessed' },
    { prefix: 'Observer-rated:',      type: 'clinician', label: 'Clinician-assessed' },
    { prefix: 'Scale:',               type: 'scale',     label: 'Validated scale'    },
  ];

  let result = null;
  for (const { prefix, type, label } of prefixMap) {
    if (method.startsWith(prefix)) {
      result = { type, label, text: method.slice(prefix.length).trim() };
      break;
    }
  }
  if (!result) result = { type: null, label: null, text: method };

  // Correct pipeline misclassification: if tagged as lab-verified but the text
  // contains an unambiguous clinical scale name, reclassify as "Validated scale".
  // Only fires on specific proper nouns / acronyms — not generic words like "test" or "score".
  if (result.type === 'lab') {
    const lower = result.text.toLowerCase();
    if (CLINICAL_SCALE_ACRONYMS.some(kw => lower.includes(kw))) {
      result = { ...result, type: 'scale', label: 'Validated scale' };
    }
  }

  return result;
}

// Show funding whenever the pipeline provides any value — including "Not stated in abstract".
// Transparency about undisclosed funding is meaningful to users.
// Only suppress when the field is truly empty/null.
function shouldShowFunding(funding) {
  if (!funding) return false;
  return isValidValue(funding.funding_source);
}

// ── Sub-components ───────────────────────────────────────────────

// Labeled key–value row used inside StudyMethodology
function MetaRow({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <Box sx={{ display: 'flex', gap: 2, py: 0.2 }}>
      <Typography sx={{
        fontSize: '0.8rem',
        color: 'text.disabled',
        fontWeight: 500,
        minWidth: '7.5rem',
        flexShrink: 0,
      }}>
        {label}
      </Typography>
      <Typography sx={secondaryText}>{value}</Typography>
    </Box>
  );
}

function ParticipantsSection({ population }) {
  if (!population) return null;

  const countLine = (population.n_intervention != null && population.n_control != null)
    ? `${population.n_intervention} intervention / ${population.n_control} control`
    : population.n_total ? `${population.n_total} participants` : null;

  const ageParts = [];
  if (population.age_range) ageParts.push(`Ages ${population.age_range}`);
  else if (population.age_mean) ageParts.push(`Mean age ${population.age_mean}`);
  if (population.pct_female != null) ageParts.push(`${population.pct_female}% female`);
  const ageLine = ageParts.length > 0 ? ageParts.join(' · ') : null;

  if (!countLine && !ageLine && !population.health_status) return null;

  return (
    <Box sx={{ ...cardSx, height: '100%' }}>
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mb: 1 }}>
        <GroupOutlined sx={iconSx} />
        <Typography sx={{ ...sectionLabel, mb: 0 }}>Participants</Typography>
      </Box>
      {countLine && <Typography sx={bodyText}>{countLine}</Typography>}
      {ageLine && <Typography sx={secondaryText}>{ageLine}</Typography>}
      {population.health_status && (
        <Typography sx={{ ...secondaryText, mt: 0.25 }}>{population.health_status}</Typography>
      )}
      {population.trial_setting && (
        <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75, alignItems: 'baseline' }}>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', fontWeight: 600, minWidth: '3.5rem', flexShrink: 0 }}>
            Setting
          </Typography>
          <Typography sx={secondaryText}>{population.trial_setting}</Typography>
        </Box>
      )}
    </Box>
  );
}

function DosingSection({ dosing }) {
  if (!dosing?.interventions?.length) return null;

  return (
    <Box sx={{ ...cardSx, height: '100%' }}>
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mb: 1 }}>
        <MedicationOutlined sx={iconSx} />
        <Typography sx={{ ...sectionLabel, mb: 0 }}>Dosing</Typography>
      </Box>
      {dosing.interventions.map((d, i) => {
        // Suppress chemical_form if it duplicates the substance name
        const showChemForm = isValidValue(d.chemical_form) &&
          d.chemical_form.trim().toLowerCase() !== (d.substance || '').trim().toLowerCase();
        const substanceLine = [
          d.substance,
          showChemForm ? `(${d.chemical_form})` : null,
        ].filter(Boolean).join(' ');

        const detailParts = [];
        if (isValidValue(d.amount)) detailParts.push(`${d.amount}${isValidValue(d.unit) ? ' ' + d.unit : ''}`);
        if (isValidValue(d.frequency)) detailParts.push(d.frequency);
        const dur = fmtDuration(d.duration_weeks);
        if (dur) detailParts.push(dur);
        if (isValidValue(d.form)) detailParts.push(d.form);
        if (isValidValue(d.route)) detailParts.push(d.route);
        if (isValidValue(d.timing)) detailParts.push(d.timing);

        return (
          <Box key={i} sx={{ mt: i > 0 ? 1 : 0 }}>
            <Typography sx={bodyText}>
              <Box component="span" sx={{ fontWeight: 600 }}>{substanceLine}</Box>
              {detailParts.length > 0 && (
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  {' · '}{detailParts.join(' · ')}
                </Box>
              )}
            </Typography>
            {isValidValue(d.ingredient_breakdown) && (
              <Typography sx={{ ...secondaryText, fontSize: '0.8rem', mt: 0.25 }}>
                {d.ingredient_breakdown}
              </Typography>
            )}
            {isValidValue(d.bioavailability_notes) && (
              <Typography sx={{ ...secondaryText, fontSize: '0.8rem', mt: 0.25, fontStyle: 'italic' }}>
                {d.bioavailability_notes}
              </Typography>
            )}
          </Box>
        );
      })}
      {isValidValue(dosing.control) && (
        <Typography sx={{ ...secondaryText, mt: 0.75, fontSize: '0.8rem' }}>
          Control: {dosing.control}
        </Typography>
      )}
    </Box>
  );
}

function SafetySection({ safety }) {
  if (!safety) return null;

  const sae = safety.serious_adverse_events;
  const saeText = (sae === 0 || sae === '0')
    ? 'No serious adverse events reported.'
    : (sae && typeof sae === 'string' && sae.trim().length > 0) ? sae : null;

  const hasContent =
    safety.safety_conclusion ||
    saeText ||
    safety.adverse_events?.length > 0 ||
    safety.contraindications_mentioned?.length > 0 ||
    safety.drug_interactions_mentioned?.length > 0;

  if (!hasContent) return null;

  return (
    <Box sx={cardSx}>
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mb: 1 }}>
        <ShieldOutlined sx={iconSx} />
        <Typography sx={{ ...sectionLabel, mb: 0 }}>Safety</Typography>
      </Box>

      {safety.safety_conclusion && (
        <Typography sx={bodyText}>{safety.safety_conclusion}</Typography>
      )}

      {/* SAEs always shown when present — independent of safety_conclusion */}
      {saeText && (
        <Typography sx={{ ...bodyText, mt: safety.safety_conclusion ? 0.5 : 0 }}>{saeText}</Typography>
      )}

      {/* Adverse events list only when no high-level conclusion covers them */}
      {!safety.safety_conclusion && safety.adverse_events?.length > 0 && (
        <Box sx={{ mt: saeText ? 0.75 : 0 }}>
          {safety.adverse_events.slice(0, 4).map((ae, i) => {
            let text = ae.event;
            if (ae.n_event_intervention != null && ae.n_total_intervention != null) {
              const pct = Math.round((ae.n_event_intervention / ae.n_total_intervention) * 100);
              text += ` (${ae.n_event_intervention}/${ae.n_total_intervention} = ${pct}%)`;
            } else if (ae.pct_intervention != null) {
              text += ` (${ae.pct_intervention}%)`;
            }
            if (ae.severity) text += ` — ${ae.severity}`;
            return (
              <Typography key={i} sx={{ ...secondaryText, mt: i > 0 ? 0.25 : 0 }}>
                • {text}
              </Typography>
            );
          })}
          {safety.ae_denominator_note && (
            <Typography sx={{ ...secondaryText, fontSize: '0.78rem', mt: 0.5, color: 'text.disabled' }}>
              {safety.ae_denominator_note}
            </Typography>
          )}
        </Box>
      )}

      {safety.contraindications_mentioned?.length > 0 && (
        <Box sx={{ mt: 0.75 }}>
          <Typography sx={{ ...secondaryText, fontWeight: 600 }}>Contraindications:</Typography>
          <Typography sx={secondaryText}>{safety.contraindications_mentioned.join(', ')}</Typography>
        </Box>
      )}
      {safety.drug_interactions_mentioned?.length > 0 && (
        <Box sx={{ mt: 0.75 }}>
          <Typography sx={{ ...secondaryText, fontWeight: 600 }}>Drug interactions:</Typography>
          <Typography sx={secondaryText}>{safety.drug_interactions_mentioned.join(', ')}</Typography>
        </Box>
      )}
    </Box>
  );
}

// Effect-size badge on a monochromatic accent ramp: magnitude reads as intensity
// of fill, not as a change of hue. DESIGN_DOC §10 (2026-08-10) — amber is
// reserved for interaction risk, so evidence-quality signalling may not spend it.
//
// The ramp lives in the FILL, and the text stays evergreen. `color.accent` as the
// label colour measures 2.7–3.3:1 on its own tints, against a 4.5:1 floor for
// 0.72rem bold — it is a fill colour, not a text colour (STACK-503). Evergreen on
// the same tints clears the floor comfortably.
//
// The exact ratio is a RANGE, not a number, and deliberately recorded as one.
// Three of these four fills are translucent, so each chip inherits whatever it
// sits on, and these chips render on more than one ground. Two independent
// sighted runs (STACK-506) measured:
//
//   ground            large  moderate  small  not-significant
//   pure white         7.95     8.86    5.20     5.61
//   color.surface      7.43     8.27    5.20     5.20
//
// `small` is identical in both because its fill is opaque `color.surface`; the
// other three move with the ground. Worst case across every ground observed is
// 5.20:1, so all four clear 4.5:1 wherever they land — which is the claim worth
// keeping. Quoting a single pair here was wrong twice before this: once computed
// over `color.paper`, which neither run saw, and once pinned to one run's ground
// as though it were the only one.
//
// (Do not write a PR reference as a bare number-sign here: `raw-hex` scans
// comments unmasked, so it reads as a 3-digit hex and fails the guard.)
//
// The provenance (author-stated / Cohen's d) is always shown alongside the badge so
// users know this reflects the study data, not Stack's editorial opinion.
function ClinicalSignificanceBadge({ cs }) {
  const parsed = parseClinicalSignificance(cs);

  // Unknown format — show the raw string verbatim rather than hiding it
  if (!parsed?.magnitude) {
    return cs ? (
      <Typography sx={secondaryText}>{cs}</Typography>
    ) : null;
  }

  const colorMap = {
    large: {
      bg: alpha(color.accent, 0.16),
      color: color.evergreen,
      border: alpha(color.accent, 0.45),
    },
    moderate: {
      bg: alpha(color.accent, 0.07),
      color: color.evergreen,
      border: alpha(color.accent, 0.22),
    },
    small: { bg: color.surface, color: color.inkSoft, border: color.line },
    'not significant': { bg: 'transparent', color: color.inkSoft, border: color.lineSoft },
  };
  const colors = colorMap[parsed.magnitude] || colorMap.small;

  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
      <Box sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1,
        py: 0.25,
        borderRadius: '0.25rem',
        backgroundColor: colors.bg,
        border: `0.0625rem solid ${colors.border}`,
        flexShrink: 0,
      }}>
        <Typography sx={{
          fontSize: '0.72rem',
          fontWeight: 700,
          color: colors.color,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}>
          {parsed.magnitude} effect
        </Typography>
      </Box>
      {/* Always show the source so users know this is from the study, not our opinion */}
      {parsed.detail && (
        <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', fontStyle: 'italic' }}>
          {parsed.detail}
        </Typography>
      )}
    </Box>
  );
}

// Collapsible study methodology with labeled key-value rows
function StudyMethodology({ pdfInsights, population }) {
  const [open, setOpen] = useState(false);
  const methodology = parseJson(pdfInsights.methodology_quality);
  const stats = parseJson(pdfInsights.statistical_results);
  const subgroups = parseJson(pdfInsights.subgroup_analyses);
  const limitations = pdfInsights.limitations;

  const subgroupLines = subgroups
    ? ['by_age', 'by_sex', 'by_baseline_severity']
        .map(key => {
          const s = subgroups[key];
          if (!s?.found || !s?.finding) return null;
          const label = key === 'by_age' ? 'Age' : key === 'by_sex' ? 'Sex' : 'Baseline severity';
          return `${label}: ${s.finding}${s.p_value != null ? ` (p = ${s.p_value})` : ''}`;
        })
        .filter(Boolean)
    : null;

  const hasContent =
    methodology?.study_design ||
    stats?.primary_outcome?.measure ||
    subgroupLines?.length > 0 ||
    limitations?.length > 0;

  if (!hasContent) return null;

  const primaryOutcome = stats?.primary_outcome;
  const ci = primaryOutcome?.ci_95;
  const ciText = Array.isArray(ci) && ci.length === 2 && ci[0] != null && ci[1] != null
    ? `[${ci[0]}, ${ci[1]}]`
    : null;

  return (
    <Box>
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          py: 0.5,
          '&:hover': { opacity: 0.7 },
        }}
      >
        <Typography sx={{
          fontSize: '0.76rem',
          fontWeight: 600,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}>
          Study Methodology
        </Typography>
        <ExpandMore sx={{
          fontSize: '1rem',
          color: 'text.secondary',
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s',
        }} />
      </Box>

      <Collapse in={open}>
        <Box sx={{
          pt: 1,
          pl: 1.5,
          borderLeft: '0.125rem solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}>

          {/* Design rows */}
          {methodology?.study_design && (
            <Box>
              <Typography sx={sectionLabel}>Design</Typography>
              <MetaRow label="Study design" value={methodology.study_design} />
              <MetaRow label="Randomization" value={methodology.randomization_method} />
              <MetaRow label="Blinding" value={methodology.blinding} />
              <MetaRow label="Allocation" value={
                methodology.allocation_concealment != null
                  ? `${boolToYesNo(methodology.allocation_concealment) || String(methodology.allocation_concealment)} (concealed)`
                  : null
              } />
              <MetaRow label="ITT analysis" value={boolToYesNo(methodology.intention_to_treat)} />
              <MetaRow label="Power calc." value={methodology.power_calculation} />
              {methodology.attrition_rate != null && (
                <MetaRow
                  label="Attrition"
                  value={[
                    fmtRate(methodology.attrition_rate),
                    methodology.attrition_intervention != null && methodology.attrition_control != null
                      ? `(intervention ${fmtRate(methodology.attrition_intervention)} / control ${fmtRate(methodology.attrition_control)})`
                      : null,
                  ].filter(Boolean).join(' ')}
                />
              )}
              {population?.baseline_balance && (
                <MetaRow label="Baseline" value={population.baseline_balance} />
              )}
              {methodology.trial_registry_id && (
                <Box sx={{ display: 'flex', gap: 2, py: 0.2 }}>
                  <Typography sx={{
                    fontSize: '0.8rem',
                    color: 'text.disabled',
                    fontWeight: 500,
                    minWidth: '7.5rem',
                    flexShrink: 0,
                  }}>
                    Registry
                  </Typography>
                  <Box
                    component="a"
                    href={`https://clinicaltrials.gov/study/${methodology.trial_registry_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontSize: '0.85rem', color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {methodology.trial_registry_id}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Statistical results */}
          {primaryOutcome?.measure && (
            <Box>
              <Typography sx={sectionLabel}>Statistical Results</Typography>
              <Typography sx={secondaryText}>
                <Box component="span" sx={{ fontWeight: 600 }}>Primary: </Box>
                {primaryOutcome.measure}
                {primaryOutcome.between_group_difference && ` — ${primaryOutcome.between_group_difference}`}
                {primaryOutcome.p_value != null && (
                  <Typography variant="data">{` (p = ${primaryOutcome.p_value})`}</Typography>
                )}
                {primaryOutcome.effect_size != null &&
                  `, ${primaryOutcome.effect_size_type || 'effect size'} = ${primaryOutcome.effect_size}`
                }
                {ciText && `, 95% CI ${ciText}`}
              </Typography>
              {stats.secondary_outcomes?.length > 0 && (
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
                  {stats.secondary_outcomes.map((o, i) => (
                    <Box component="li" key={i} sx={secondaryText}>
                      {o.measure}: {o.between_group_difference || ''}
                      {o.p_value != null && (
                        <Typography variant="data">{` (p = ${o.p_value})`}</Typography>
                      )}
                      {Array.isArray(o.ci_95) && o.ci_95.length === 2 && o.ci_95[0] != null && o.ci_95[1] != null ? `, 95% CI [${o.ci_95[0]}, ${o.ci_95[1]}]` : ''}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Subgroup analyses */}
          {subgroupLines?.length > 0 && (
            <Box>
              <Typography sx={sectionLabel}>Subgroup Analyses</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {subgroupLines.map((line, i) => (
                  // Prebuilt strings carrying an inline p-value, so the whole
                  // line takes tabular figures rather than a wrapped number.
                  <Box component="li" key={i} sx={{ ...secondaryText, ...tabularNums }}>{line}</Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Limitations */}
          {limitations?.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
              <WarningAmberIcon sx={{ fontSize: '0.85rem', color: 'text.disabled', mt: '0.15rem', flexShrink: 0 }} />
              <Box>
                <Typography sx={sectionLabel}>Limitations</Typography>
                <Box component="ul" sx={{ m: 0, pl: 2, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  {limitations.map((l, i) => (
                    <Box component="li" key={i} sx={secondaryText}>{l}</Box>
                  ))}
                </Box>
              </Box>
            </Box>
          )}

        </Box>
      </Collapse>
    </Box>
  );
}

// ── Main export ──────────────────────────────────────────────────

export default function ArticlePdfView({ article }) {
  const pdfInsights = article.pdf_insights;
  if (!pdfInsights) return null;

  const dosing = parseJson(pdfInsights.dosing_protocol);
  const population = parseJson(pdfInsights.population_details);
  const safety = parseJson(pdfInsights.safety_profile);
  const stats = parseJson(pdfInsights.statistical_results);
  const funding = parseJson(pdfInsights.funding_and_conflicts);
  const methodology = parseJson(pdfInsights.methodology_quality);

  const primaryOutcome = stats?.primary_outcome;
  const clinicalSig = primaryOutcome?.clinical_significance;
  const showFunding = shouldShowFunding(funding);

  const hasSummary = pdfInsights.plain_summary || pdfInsights.key_findings?.length > 0;
  const hasStudySetup = population || dosing?.interventions?.length > 0;
  const hasBiomarker = primaryOutcome?.measure || primaryOutcome?.primary_measurement_method;

  const measurementMethod = parseMeasurementMethod(primaryOutcome?.primary_measurement_method);
  const ci = primaryOutcome?.ci_95;
  const ciPlain = ciToPlainLanguage(ci);
  const attritionRate = methodology?.attrition_rate;
  const attritionText = attritionLabel(attritionRate);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 2.5 } }}>

      {/* 1. Enhanced Summary — narrative + key findings bullets */}
      {hasSummary && (
        <Box sx={{
          py: 1.5,
          px: 2,
          backgroundColor: 'rgba(46, 125, 50, 0.06)',
          borderRadius: '0.5rem',
          borderLeft: '0.1875rem solid',
          borderColor: 'success.light',
        }}>
          <Typography sx={{
            fontSize: '0.7rem',
            fontWeight: 600,
            color: 'success.dark',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            mb: 0.75,
          }}>
            Enhanced Summary
          </Typography>
          {pdfInsights.plain_summary && (
            <Typography sx={{ ...bodyText, fontWeight: 500 }}>
              {pdfInsights.plain_summary}
            </Typography>
          )}
          {pdfInsights.key_findings?.length > 0 && (
            <Box
              component="ul"
              sx={{
                m: 0,
                mt: pdfInsights.plain_summary ? 1 : 0,
                pl: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.35,
              }}
            >
              {pdfInsights.key_findings.map((f, i) => (
                <Box component="li" key={i} sx={secondaryText}>{f}</Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* 2. Participants | Dosing — 2-col on desktop, stacked on mobile */}
      {hasStudySetup && (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
          alignItems: 'start',
        }}>
          <ParticipantsSection population={population} />
          <DosingSection dosing={dosing} />
        </Box>
      )}

      {/* 3. Primary Outcome / Biomarker */}
      {hasBiomarker && (
        <Box sx={cardSx}>
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mb: 1 }}>
            <AssessmentOutlined sx={iconSx} />
            <Typography sx={{ ...sectionLabel, mb: 0 }}>Primary Outcome</Typography>
          </Box>
          {primaryOutcome.measure && (
            <Typography sx={{ ...bodyText, fontWeight: 600, mb: measurementMethod ? 0.5 : 0 }}>
              {primaryOutcome.measure}
            </Typography>
          )}
          {measurementMethod && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {measurementMethod.label && (
                <Box sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  px: 0.75,
                  py: 0.2,
                  borderRadius: '0.25rem',
                  backgroundColor:
                    measurementMethod.type === 'lab' ? 'rgba(25, 118, 210, 0.08)' :
                    (measurementMethod.type === 'clinician' || measurementMethod.type === 'scale') ? alpha(color.evergreen, 0.08) :
                    color.surface,
                  border: '0.0625rem solid',
                  borderColor:
                    measurementMethod.type === 'lab' ? 'rgba(25, 118, 210, 0.25)' :
                    (measurementMethod.type === 'clinician' || measurementMethod.type === 'scale') ? alpha(color.evergreen, 0.25) :
                    color.line,
                }}>
                  <Typography sx={{
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color:
                      measurementMethod.type === 'lab' ? 'primary.dark' :
                      (measurementMethod.type === 'clinician' || measurementMethod.type === 'scale') ? color.evergreen :
                      'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  }}>
                    {measurementMethod.label}
                  </Typography>
                </Box>
              )}
              <Typography sx={secondaryText}>{measurementMethod.text}</Typography>
            </Box>
          )}
          {/* Result: between-group difference + p-value */}
          {(primaryOutcome.between_group_difference || primaryOutcome.p_value != null) && (
            <Typography sx={{ ...secondaryText, ...tabularNums, mt: 0.5 }}>
              {[
                primaryOutcome.between_group_difference || null,
                primaryOutcome.p_value != null ? `p = ${primaryOutcome.p_value}` : null,
              ].filter(Boolean).join('  ·  ')}
            </Typography>
          )}
          {/* Plain-language CI reliability statement */}
          {ciPlain && (
            <Typography sx={{ ...secondaryText, mt: 0.25, fontSize: '0.8rem', fontStyle: 'italic' }}>
              {ciPlain}
            </Typography>
          )}
          {/* Dropout context */}
          {attritionText && (
            <Typography sx={{ ...secondaryText, mt: 0.5, fontSize: '0.8rem' }}>
              <Box component="span" sx={{ fontWeight: 600 }}>Dropout: </Box>
              {fmtRate(attritionRate)} — {attritionText}
            </Typography>
          )}
        </Box>
      )}

      {/* 4. Safety */}
      <SafetySection safety={safety} />

      {/* 5. Clinical Takeaway — effect size badge + practical insights */}
      {(clinicalSig || pdfInsights.clinical_insights?.length > 0) && (
        <Box>
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mb: 0.5 }}>
            <LightbulbOutlined sx={iconSx} />
            <Typography sx={{ ...sectionLabel, mb: 0 }}>Clinical Takeaway</Typography>
          </Box>
          {clinicalSig && (
            <Box sx={{ mb: pdfInsights.clinical_insights?.length > 0 ? 1 : 0 }}>
              <ClinicalSignificanceBadge cs={clinicalSig} />
            </Box>
          )}
          {pdfInsights.clinical_insights?.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {pdfInsights.clinical_insights.map((insight, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                  <CheckCircleOutline sx={{ fontSize: '0.9rem', color: 'success.light', mt: '0.2rem', flexShrink: 0 }} />
                  <Typography sx={bodyText}>{insight}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* 6. Funded by — only shown when a source is explicitly stated.
          Neutral surface, not an amber tint: DESIGN_DOC §10 (2026-08-10). The
          WarningAmberIcon and the words carry the caution; the tint was saying
          nothing they do not, at the cost of a hue reserved for interaction risk. */}
      {showFunding && (
        <Box sx={{
          py: 1.25,
          px: 1.5,
          backgroundColor: color.surface,
          borderRadius: '0.5rem',
          border: `0.0625rem solid ${color.line}`,
        }}>
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mb: 0.5 }}>
            <WarningAmberIcon sx={{ fontSize: '0.9rem', color: 'warning.main', flexShrink: 0 }} />
            <Typography sx={{ ...sectionLabel, mb: 0, color: 'warning.dark' }}>
              Funded by
            </Typography>
          </Box>
          <Typography sx={secondaryText}>
            {funding.funding_source}
            {funding.industry_funded === true && ' (industry funded)'}
          </Typography>
        </Box>
      )}

      {/* 7. Study Methodology — collapsible */}
      <StudyMethodology pdfInsights={pdfInsights} population={population} />

    </Box>
  );
}
